import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';

import CaloriesSection from 'components/shared/nutrition_facts/CaloriesSection';
import CarbohydratesSection from 'components/shared/nutrition_facts/CarbohydratesSection';
import FatSection from 'components/shared/nutrition_facts/FatSection';
import { fontStyles } from 'components/shared/nutrition_facts/sharedStyles';
import MainNutritionRow from 'components/shared/nutrition_facts/MainNutritionRow';
import NutritionPanelDisclaimer from 'components/shared/nutrition_facts/NutritionPanelDisclaimer';
import NutritionPanelIngredients from 'components/shared/nutrition_facts/NutritionPanelIngredients';
import Separator, {
  MEDIUM_SEPARATOR_THICKNESS,
  LARGE_SEPARATOR_THICKNESS,
} from 'components/shared/nutrition_facts/Separator';
import ServingsSection from 'components/shared/nutrition_facts/ServingsSection';
import SubNutritionRow from 'components/shared/nutrition_facts/SubNutritionRow';
import useFindAttributeValueByName from 'lib/hooks/product/useFindAttributeValueByName';

const NUTRITION_ATTRIBUTES = {
  NUTRITION_PANEL_DISCLAIMER: 'nutrition_panel_disclaimer',
  SERVING_SIZE_QUANTITY: 'serving_size_quantity',
  SERVING_SIZE_UOM: 'serving_size_uom',
  SERVING_SIZE_DESCRIPTION: 'serving_size_description',
  NUMBER_OF_SERVINGS_PER_PACKAGE: 'number_of_servings_per_package',
  CALORIES_QUANTITYCONTAINED: 'calories_quantitycontained',
  CHOLESTEROL_QUANTITYCONTAINED: 'cholesterol_quantitycontained',
  CHOLESTEROL_UOM: 'cholesterol_uom',
  CHOLESTEROL_MEASUREMENTPRECISIONCODE: 'cholesterol_measurementprecisioncode',
  CHOLESTEROL_RDI_DAILYVALUEINTAKEPERCENT: 'cholesterol_rdi_dailyvalueintakepercent',
  SODIUM_QUANTITYCONTAINED: 'sodium_quantitycontained',
  SODIUM_UOM: 'sodium_uom',
  SODIUM_MEASUREMENTPRECISIONCODE: 'sodium_measurementprecisioncode',
  SODIUM_RDI_DAILYVALUEINTAKEPERCENT: 'sodium_rdi_dailyvalueintakepercent',
  PROTEIN_QUANTITYCONTAINED: 'protein_quantitycontained',
  PROTEIN_UOM: 'protein_uom',
  PROTEIN_MEASUREMENTPRECISIONCODE: 'protein_measurementprecisioncode',
  PROTEIN_RDI_DAILYVALUEINTAKEPERCENT: 'protein_rdi_dailyvalueintakepercent',
  VITAMIN_D_QUANTITYCONTAINED: 'vitamin_d_quantitycontained',
  VITAMIN_D_UOM: 'vitamin_d_uom',
  VITAMIN_D_MEASUREMENTPRECISIONCODE: 'vitamin_d_measurementprecisioncode',
  VITAMIN_D_RDI_DAILYVALUEINTAKEPERCENT: 'vitamin_d_rdi_dailyvalueintakepercent',
  CALCIUM_QUANTITYCONTAINED: 'calcium_quantitycontained',
  CALCIUM_UOM: 'calcium_uom',
  CALCIUM_MEASUREMENTPRECISIONCODE: 'calcium_measurementprecisioncode',
  CALCIUM_RDI_DAILYVALUEINTAKEPERCENT: 'calcium_rdi_dailyvalueintakepercent',
  IRON_QUANTITYCONTAINED: 'iron_quantitycontained',
  IRON_UOM: 'iron_uom',
  IRON_MEASUREMENTPRECISIONCODE: 'iron_measurementprecisioncode',
  IRON_RDI_DAILYVALUEINTAKEPERCENT: 'iron_rdi_dailyvalueintakepercent',
  POTASSIUM_QUANTITYCONTAINED: 'potassium_quantitycontained',
  POTASSIUM_UOM: 'potassium_uom',
  POTASSIUM_MEASUREMENTPRECISIONCODE: 'potassium_measurementprecisioncode',
  POTASSIUM_RDI_DAILYVALUEINTAKEPERCENT: 'potassium_rdi_dailyvalueintakepercent',
  VITAMIN_A_QUANTITYCONTAINED: 'vitamin_a_quantitycontained',
  VITAMIN_A_UOM: 'vitamin_a_uom',
  VITAMIN_A_MEASUREMENTPRECISIONCODE: 'vitamin_a_measurementprecisioncode',
  VITAMIN_A_RDI_DAILYVALUEINTAKEPERCENT: 'vitamin_a_rdi_dailyvalueintakepercent',
  VITAMIN_C_QUANTITYCONTAINED: 'vitamin_c_quantitycontained',
  VITAMIN_C_UOM: 'vitamin_c_uom',
  VITAMIN_C_MEASUREMENTPRECISIONCODE: 'vitamin_c_measurementprecisioncode',
  VITAMIN_C_RDI_DAILYVALUEINTAKEPERCENT: 'vitamin_c_rdi_dailyvalueintakepercent',
  VITAMIN_K_QUANTITYCONTAINED: 'vitamin_k_quantitycontained',
  VITAMIN_K_UOM: 'vitamin_k_uom',
  VITAMIN_K_MEASUREMENTPRECISIONCODE: 'vitamin_k_measurementprecisioncode',
  VITAMIN_K_RDI_DAILYVALUEINTAKEPERCENT: 'vitamin_k_rdi_dailyvalueintakepercent',
  VITAMIN_E_QUANTITYCONTAINED: 'vitamin_e_quantitycontained',
  VITAMIN_E_UOM: 'vitamin_e_uom',
  VITAMIN_E_MEASUREMENTPRECISIONCODE: 'vitamin_e_measurementprecisioncode',
  VITAMIN_E_RDI_DAILYVALUEINTAKEPERCENT: 'vitamin_e_rdi_dailyvalueintakepercent',
  THIAMIN_QUANTITYCONTAINED: 'thiamin_quantitycontained',
  THIAMIN_UOM: 'thiamin_uom',
  THIAMIN_MEASUREMENTPRECISIONCODE: 'thiamin_measurementprecisioncode',
  THIAMIN_RDI_DAILYVALUEINTAKEPERCENT: 'thiamin_rdi_dailyvalueintakepercent',
  RIBOFLAVIN_B2_QUANTITYCONTAINED: 'riboflavin_b2_quantitycontained',
  RIBOFLAVIN_B2_UOM: 'riboflavin_b2_uom',
  RIBOFLAVIN_B2_MEASUREMENTPRECISIONCODE: 'riboflavin_b2_measurementprecisioncode',
  RIBOFLAVIN_B2_RDI_DAILYVALUEINTAKEPERCENT: 'riboflavin_b2_rdi_dailyvalueintakepercent',
  NIACIN_QUANTITYCONTAINED: 'niacin_quantitycontained',
  NIACIN_UOM: 'niacin_uom',
  NIACIN_MEASUREMENTPRECISIONCODE: 'niacin_measurementprecisioncode',
  NIACIN_RDI_DAILYVALUEINTAKEPERCENT: 'niacin_rdi_dailyvalueintakepercent',
  VITAMIN_B6_QUANTITYCONTAINED: 'vitamin_b6_quantitycontained',
  VITAMIN_B6_UOM: 'vitamin_b6_uom',
  VITAMIN_B6_MEASUREMENTPRECISIONCODE: 'vitamin_b6_measurementprecisioncode',
  VITAMIN_B6_RDI_DAILYVALUEINTAKEPERCENT: 'vitamin_b6_rdi_dailyvalueintakepercent',
  FOLATE_QUANTITYCONTAINED: 'folate_quantitycontained',
  FOLATE_UOM: 'folate_uom',
  FOLATE_MEASUREMENTPRECISIONCODE: 'folate_measurementprecisioncode',
  FOLATE_RDI_DAILYVALUEINTAKEPERCENT: 'folate_rdi_dailyvalueintakepercent',
  FOLIC_ACID_QUANTITYCONTAINED: 'vitamin_b9_folic_acid_quantitycontained',
  FOLIC_ACID_UOM: 'vitamin_b9_folic_acid_uom',
  FOLIC_ACID_MEASUREMENTPRECISIONCODE: 'vitamin_b9_folic_acid_measurementprecisioncode',
  FOLIC_ACID_RDI_DAILYVALUEINTAKEPERCENT: 'vitamin_b9_folic_acid_rdi_dailyvalueintakepercent',
  VITAMIN_B12_QUANTITYCONTAINED: 'vitamin_b12_quantitycontained',
  VITAMIN_B12_UOM: 'vitamin_b12_uom',
  VITAMIN_B12_MEASUREMENTPRECISIONCODE: 'vitamin_b12_measurementprecisioncode',
  VITAMIN_B12_RDI_DAILYVALUEINTAKEPERCENT: 'vitamin_b12_rdi_dailyvalueintakepercent',
  CHOLINE_QUANTITYCONTAINED: 'total_choline_quantitycontained',
  CHOLINE_UOM: 'total_choline_uom',
  CHOLINE_MEASUREMENTPRECISIONCODE: 'total_choline_measurementprecisioncode',
  CHOLINE_RDI_DAILYVALUEINTAKEPERCENT: 'total_choline_rdi_dailyvalueintakepercent',
  PANTOTHENIC_ACID_QUANTITYCONTAINED: 'pantothenic_acid_quantitycontained',
  PANTOTHENIC_ACID_UOM: 'pantothenic_acid_uom',
  PANTOTHENIC_ACID_MEASUREMENTPRECISIONCODE: 'pantothenic_acid_measurementprecisioncode',
  PANTOTHENIC_ACID_RDI_DAILYVALUEINTAKEPERCENT: 'pantothenic_acid_dv_dailyvalueintakepercent',
  PHOSPHOROUS_QUANTITYCONTAINED: 'phosphorous_quantitycontained',
  PHOSPHOROUS_UOM: 'phosphorous_uom',
  PHOSPHOROUS_MEASUREMENTPRECISIONCODE: 'phosphorous_measurementprecisioncode',
  PHOSPHOROUS_RDI_DAILYVALUEINTAKEPERCENT: 'phosphorous_rdi_dailyvalueintakepercent',
  MAGNESIUM_QUANTITYCONTAINED: 'magnesium_quantitycontained',
  MAGNESIUM_UOM: 'magnesium_uom',
  MAGNESIUM_MEASUREMENTPRECISIONCODE: 'magnesium_measurementprecisioncode',
  MAGNESIUM_RDI_DAILYVALUEINTAKEPERCENT: 'magnesium_rdi_dailyvalueintakepercent',
  ZINC_QUANTITYCONTAINED: 'zinc_quantitycontained',
  ZINC_UOM: 'zinc_uom',
  ZINC_MEASUREMENTPRECISIONCODE: 'zinc_measurementprecisioncode',
  ZINC_RDI_DAILYVALUEINTAKEPERCENT: 'zinc_rdi_dailyvalueintakepercent',
  MANGANESE_QUANTITYCONTAINED: 'manganese_quantitycontained',
  MANGANESE_UOM: 'manganese_uom',
  MANGANESE_MEASUREMENTPRECISIONCODE: 'manganese_measurementprecisioncode',
  MANGANESE_RDI_DAILYVALUEINTAKEPERCENT: 'manganese_dv_rdi_usa_dailyvalueintakepercent',
  COPPER_QUANTITYCONTAINED: 'copper_quantitycontained',
  COPPER_UOM: 'copper_uom',
  COPPER_MEASUREMENTPRECISIONCODE: 'copper_measurementprecisioncode',
  COPPER_RDI_DAILYVALUEINTAKEPERCENT: 'copper_rdi_dailyvalueintakepercent',
  SELENIUM_QUANTITYCONTAINED: 'selenium_quantitycontained',
  SELENIUM_UOM: 'selenium_uom',
  SELENIUM_MEASUREMENTPRECISIONCODE: 'selenium_measurementprecisioncode',
  SELENIUM_RDI_DAILYVALUEINTAKEPERCENT: 'selenium_rdi_dailyvalueintakepercent',
  TOTAL_FAT_QUANTITYCONTAINED: 'total_fat_quantitycontained',
  TOTAL_FAT_UOM: 'total_fat_uom',
  TOTAL_FAT_MEASUREMENTPRECISIONCODE: 'total_fat_measurementprecisioncode',
  TOTAL_FAT_RDI_DAILYVALUEINTAKEPERCENT: 'total_fat_rdi_dailyvalueintakepercent',
  SATURATED_FAT_QUANTITYCONTAINED: 'saturated_fat_quantitycontained',
  SATURATED_FAT_UOM: 'saturated_fat_uom',
  SATURATED_FAT_MEASUREMENTPRECISIONCODE: 'saturated_fat_measurementprecisioncode',
  SATURATED_FAT_RDI_DAILYVALUEINTAKEPERCENT: 'saturated_fat_rdi_dailyvalueintakepercent',
  TRANSFATTY_ACIDS_QUANTITYCONTAINED: 'transfatty_acids_quantitycontained',
  TRANSFATTY_ACIDS_UOM: 'transfatty_acids_uom',
  TRANSFATTY_ACIDS_MEASUREMENTPRECISIONCODE: 'transfatty_acids_measurementprecisioncode',
  POLYUNSATURATED_FAT_QUANTITYCONTAINED: 'polyunsaturated_fat_quantitycontained',
  POLYUNSATURATED_FAT_UOM: 'polyunsaturated_fat_uom',
  POLYUNSATURATED_FAT_MEASUREMENTPRECISIONCODE: 'polyunsaturated_fat_measurementprecisioncode',
  MONOUNSATURATED_FAT_QUANTITYCONTAINED: 'monounsaturated_fat_quantitycontained',
  MONOUNSATURATED_FAT_UOM: 'monounsaturated_fat_uom',
  MONOUNSATURATED_FAT_MEASUREMENTPRECISIONCODE: 'monounsaturated_fat_measurementprecisioncode',
  CARBOHYDRATES_QUANTITYCONTAINED: 'carbohydrates_quantitycontained',
  CARBOHYDRATES_UOM: 'carbohydrates_uom',
  CARBOHYDRATES_MEASUREMENTPRECISIONCODE: 'carbohydrates_measurementprecisioncode',
  CARBOHYDRATES_RDI_DAILYVALUEINTAKEPERCENT: 'carbohydrates_rdi_dailyvalueintakepercent',
  TOTAL_DIETARY_FIBER_QUANTITYCONTAINED: 'total_dietary_fiber_quantitycontained',
  TOTAL_DIETARY_FIBER_UOM: 'total_dietary_fiber_uom',
  TOTAL_DIETARY_FIBER_MEASUREMENTPRECISIONCODE: 'total_dietary_fiber_measurementprecisioncode',
  TOTAL_DIETARY_FIBER_RDI_DAILYVALUEINTAKEPERCENT:
    'total_dietary_fiber_rdi_dailyvalueintakepercent',
  SOLUBLE_FIBER_QUANTITYCONTAINED: 'soluble_fiber_quantitycontained',
  SOLUBLE_FIBER_UOM: 'soluble_fiber_uom',
  SOLUBLE_FIBER_MEASUREMENTPRECISIONCODE: 'soluble_fiber_measurementprecisioncode',
  INSOLUBLE_FIBER_QUANTITYCONTAINED: 'insoluble_fiber_quantitycontained',
  INSOLUBLE_FIBER_UOM: 'insoluble_fiber_uom',
  INSOLUBLE_FIBER_MEASUREMENTPRECISIONCODE: 'insoluble_fiber_measurementprecisioncode',
  TOTAL_SUGAR_QUANTITYCONTAINED: 'total_sugar_quantitycontained',
  TOTAL_SUGAR_UOM: 'total_sugar_uom',
  TOTAL_SUGAR_MEASUREMENTPRECISIONCODE: 'total_sugar_measurementprecisioncode',
  ADDED_SUGAR_QUANTITYCONTAINED: 'added_sugar_quantitycontained',
  ADDED_SUGAR_UOM: 'added_sugar_uom',
  ADDED_SUGAR_RDI_DAILYVALUEINTAKEPERCENT: 'added_sugar_rdi_dailyvalueintakepercent',
  ADDED_SUGAR_MEASUREMENTPRECISIONCODE: 'added_sugar_measurementprecisioncode',
};

function ProductNutritionFactsView({ nutritionAttributes, ingredients }) {
  const servingSizeQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SERVING_SIZE_QUANTITY,
  );
  const servingSizeUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SERVING_SIZE_UOM,
  );
  const servingSizeDescription = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SERVING_SIZE_DESCRIPTION,
  );
  const servingsPerPackage = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.NUMBER_OF_SERVINGS_PER_PACKAGE,
  );
  const calories = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CALORIES_QUANTITYCONTAINED,
  );
  // Cholesterol attributes
  const cholesterolQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CHOLESTEROL_QUANTITYCONTAINED,
  );
  const cholesterolUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CHOLESTEROL_UOM,
  );
  const cholesterolDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CHOLESTEROL_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const cholesterolPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CHOLESTEROL_MEASUREMENTPRECISIONCODE,
  );

  // Sodium attributes
  const sodiumQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SODIUM_QUANTITYCONTAINED,
  );
  const sodiumUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SODIUM_UOM,
  );
  const sodiumDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SODIUM_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const sodiumPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SODIUM_MEASUREMENTPRECISIONCODE,
  );

  // USDA attribute disclaimer. Not a nutrient row.
  const panelDisclaimer = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.NUTRITION_PANEL_DISCLAIMER,
  );

  // Protein attributes
  const proteinQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PROTEIN_QUANTITYCONTAINED,
  );
  const proteinUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PROTEIN_UOM,
  );
  const proteinDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PROTEIN_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const proteinPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PROTEIN_MEASUREMENTPRECISIONCODE,
  );

  // Vitamin D attributes
  const vitaminDQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_D_QUANTITYCONTAINED,
  );
  const vitaminDUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_D_UOM,
  );
  const vitaminDDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_D_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const vitaminDPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_D_MEASUREMENTPRECISIONCODE,
  );

  // Calcium attributes
  const calciumQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CALCIUM_QUANTITYCONTAINED,
  );
  const calciumUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CALCIUM_UOM,
  );
  const calciumDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CALCIUM_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const calciumPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CALCIUM_MEASUREMENTPRECISIONCODE,
  );

  // Iron attributes
  const ironQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.IRON_QUANTITYCONTAINED,
  );
  const ironUom = useFindAttributeValueByName(nutritionAttributes, NUTRITION_ATTRIBUTES.IRON_UOM);
  const ironDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.IRON_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const ironPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.IRON_MEASUREMENTPRECISIONCODE,
  );

  // Potassium attributes
  const potassiumQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.POTASSIUM_QUANTITYCONTAINED,
  );
  const potassiumUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.POTASSIUM_UOM,
  );
  const potassiumDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.POTASSIUM_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const potassiumPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.POTASSIUM_MEASUREMENTPRECISIONCODE,
  );

  // Vitamin A attributes
  const vitaminAQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_A_QUANTITYCONTAINED,
  );
  const vitaminAUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_A_UOM,
  );
  const vitaminADailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_A_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const vitaminAPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_A_MEASUREMENTPRECISIONCODE,
  );

  // Vitamin C attributes
  const vitaminCQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_C_QUANTITYCONTAINED,
  );
  const vitaminCUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_C_UOM,
  );
  const vitaminCDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_C_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const vitaminCPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_C_MEASUREMENTPRECISIONCODE,
  );

  // Vitamin E attributes
  const vitaminEQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_E_QUANTITYCONTAINED,
  );
  const vitaminEUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_E_UOM,
  );
  const vitaminEDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_E_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const vitaminEPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_E_MEASUREMENTPRECISIONCODE,
  );

  // Vitamin K attributes
  const vitaminKQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_K_QUANTITYCONTAINED,
  );
  const vitaminKUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_K_UOM,
  );
  const vitaminKDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_K_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const vitaminKPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_K_MEASUREMENTPRECISIONCODE,
  );

  // Thiamin attributes
  const thiaminQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.THIAMIN_QUANTITYCONTAINED,
  );
  const thiaminUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.THIAMIN_UOM,
  );
  const thiaminDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.THIAMIN_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const thiaminPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.THIAMIN_MEASUREMENTPRECISIONCODE,
  );

  // Riboflavin attributes
  const riboflavinQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.RIBOFLAVIN_B2_QUANTITYCONTAINED,
  );
  const riboflavinUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.RIBOFLAVIN_B2_UOM,
  );
  const riboflavinDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.RIBOFLAVIN_B2_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const riboflavinPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.RIBOFLAVIN_B2_MEASUREMENTPRECISIONCODE,
  );

  // Niacin attributes
  const niacinQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.NIACIN_QUANTITYCONTAINED,
  );
  const niacinUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.NIACIN_UOM,
  );
  const niacinDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.NIACIN_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const niacinPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.NIACIN_MEASUREMENTPRECISIONCODE,
  );

  // Vitamin B6 attributes
  const vitaminB6Quantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B6_QUANTITYCONTAINED,
  );
  const vitaminB6Uom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B6_UOM,
  );
  const vitaminB6DailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B6_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const vitaminB6PrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B6_MEASUREMENTPRECISIONCODE,
  );

  // Folate attributes
  const folateQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.FOLATE_QUANTITYCONTAINED,
  );
  const folateUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.FOLATE_UOM,
  );
  const folateDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.FOLATE_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const folatePrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.FOLATE_MEASUREMENTPRECISIONCODE,
  );

  // Folic Acid attributes
  const folicAcidQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.FOLIC_ACID_QUANTITYCONTAINED,
  );
  const folicAcidUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.FOLIC_ACID_UOM,
  );
  const folicAcidDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.FOLIC_ACID_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const folicAcidPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.FOLIC_ACID_MEASUREMENTPRECISIONCODE,
  );

  // Vitamin B12 attributes
  const vitaminB12Quantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B12_QUANTITYCONTAINED,
  );
  const vitaminB12Uom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B12_UOM,
  );
  const vitaminB12DailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B12_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const vitaminB12PrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B12_MEASUREMENTPRECISIONCODE,
  );

  // Pantothenic Acid attributes
  const pantothenicAcidQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PANTOTHENIC_ACID_QUANTITYCONTAINED,
  );
  const pantothenicAcidUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PANTOTHENIC_ACID_UOM,
  );
  const pantothenicAcidDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PANTOTHENIC_ACID_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const pantothenicAcidPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PANTOTHENIC_ACID_MEASUREMENTPRECISIONCODE,
  );

  // Choline attributes
  const cholineQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CHOLINE_QUANTITYCONTAINED,
  );
  const cholineUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CHOLINE_UOM,
  );
  const cholineDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CHOLINE_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const cholinePrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CHOLINE_MEASUREMENTPRECISIONCODE,
  );

  // Phosphorus attributes
  const phosphorousQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PHOSPHOROUS_QUANTITYCONTAINED,
  );
  const phosphorousUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PHOSPHOROUS_UOM,
  );
  const phosphorousDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PHOSPHOROUS_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const phosphorousPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PHOSPHOROUS_MEASUREMENTPRECISIONCODE,
  );

  // Magnesium attributes
  const magnesiumQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.MAGNESIUM_QUANTITYCONTAINED,
  );
  const magnesiumUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.MAGNESIUM_UOM,
  );
  const magnesiumDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.MAGNESIUM_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const magnesiumPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.MAGNESIUM_MEASUREMENTPRECISIONCODE,
  );

  // Zinc attributes
  const zincQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.ZINC_QUANTITYCONTAINED,
  );
  const zincUom = useFindAttributeValueByName(nutritionAttributes, NUTRITION_ATTRIBUTES.ZINC_UOM);
  const zincDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.ZINC_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const zincPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.ZINC_MEASUREMENTPRECISIONCODE,
  );

  // Selenium attributes
  const seleniumQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SELENIUM_QUANTITYCONTAINED,
  );
  const seleniumUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SELENIUM_UOM,
  );
  const seleniumDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SELENIUM_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const seleniumPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SELENIUM_MEASUREMENTPRECISIONCODE,
  );

  // Copper attributes
  const copperQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.COPPER_QUANTITYCONTAINED,
  );
  const copperUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.COPPER_UOM,
  );
  const copperDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.COPPER_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const copperPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.COPPER_MEASUREMENTPRECISIONCODE,
  );

  // Manganese attributes
  const manganeseQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.MANGANESE_QUANTITYCONTAINED,
  );
  const manganeseUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.MANGANESE_UOM,
  );
  const manganeseDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.MANGANESE_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const manganesePrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.MANGANESE_MEASUREMENTPRECISIONCODE,
  );

  if (!nutritionAttributes?.length) {
    return null;
  }

  return (
    <>
      <NutritionPanelDisclaimer
        disclaimer={panelDisclaimer}
        className={classNames('border border-dark p-2 mb-2', fontStyles('12px'))}
      />
      <div className="border border-dark p-2">
        <div className={fontStyles('44px', true, true)}>Nutrition Facts</div>
        <Separator />

        <ServingsSection
          servingsPerPackage={servingsPerPackage}
          description={servingSizeDescription}
          quantity={servingSizeQuantity}
          uom={servingSizeUom}
        />
        <Separator thickness={LARGE_SEPARATOR_THICKNESS} />

        <CaloriesSection calories={calories} />
        <Separator thickness={MEDIUM_SEPARATOR_THICKNESS} />

        <div className={classNames('w-100 text-right', fontStyles('12px', true))}>
          % Daily Value*
        </div>
        <Separator />

        <FatSection nutritionAttributes={nutritionAttributes} />

        <MainNutritionRow
          title="Cholesterol"
          quantity={cholesterolQuantity}
          uom={cholesterolUom}
          percentage={cholesterolDailyValue}
          measurementPrecisionCode={cholesterolPrecisionCode}
        />

        <MainNutritionRow
          title="Sodium"
          quantity={sodiumQuantity}
          uom={sodiumUom}
          percentage={sodiumDailyValue}
          measurementPrecisionCode={sodiumPrecisionCode}
        />

        <CarbohydratesSection nutritionAttributes={nutritionAttributes} />

        <MainNutritionRow
          title="Protein"
          quantity={proteinQuantity}
          uom={proteinUom}
          percentage={proteinDailyValue}
          measurementPrecisionCode={proteinPrecisionCode}
        />

        <Separator thickness={LARGE_SEPARATOR_THICKNESS} />

        <SubNutritionRow
          title="Vitamin D"
          quantity={vitaminDQuantity}
          uom={vitaminDUom}
          percentage={vitaminDDailyValue}
          measurementPrecisionCode={vitaminDPrecisionCode}
        />
        <SubNutritionRow
          title="Calcium"
          quantity={calciumQuantity}
          uom={calciumUom}
          percentage={calciumDailyValue}
          measurementPrecisionCode={calciumPrecisionCode}
        />
        <SubNutritionRow
          title="Iron"
          quantity={ironQuantity}
          uom={ironUom}
          percentage={ironDailyValue}
          measurementPrecisionCode={ironPrecisionCode}
        />
        <SubNutritionRow
          title="Potassium"
          quantity={potassiumQuantity}
          uom={potassiumUom}
          percentage={potassiumDailyValue}
          measurementPrecisionCode={potassiumPrecisionCode}
        />
        <SubNutritionRow
          title="Vitamin A"
          quantity={vitaminAQuantity}
          uom={vitaminAUom}
          percentage={vitaminADailyValue}
          measurementPrecisionCode={vitaminAPrecisionCode}
        />
        <SubNutritionRow
          title="Vitamin C"
          quantity={vitaminCQuantity}
          uom={vitaminCUom}
          percentage={vitaminCDailyValue}
          measurementPrecisionCode={vitaminCPrecisionCode}
        />
        <SubNutritionRow
          title="Vitamin E"
          quantity={vitaminEQuantity}
          uom={vitaminEUom}
          percentage={vitaminEDailyValue}
          measurementPrecisionCode={vitaminEPrecisionCode}
        />
        <SubNutritionRow
          title="Vitamin K"
          quantity={vitaminKQuantity}
          uom={vitaminKUom}
          percentage={vitaminKDailyValue}
          measurementPrecisionCode={vitaminKPrecisionCode}
        />
        <SubNutritionRow
          title="Thiamin"
          quantity={thiaminQuantity}
          uom={thiaminUom}
          percentage={thiaminDailyValue}
          measurementPrecisionCode={thiaminPrecisionCode}
        />
        <SubNutritionRow
          title="Riboflavin"
          quantity={riboflavinQuantity}
          uom={riboflavinUom}
          percentage={riboflavinDailyValue}
          measurementPrecisionCode={riboflavinPrecisionCode}
        />
        <SubNutritionRow
          title="Niacin"
          quantity={niacinQuantity}
          uom={niacinUom}
          percentage={niacinDailyValue}
          measurementPrecisionCode={niacinPrecisionCode}
        />
        <SubNutritionRow
          title={
            <span>
              Vitamin B<sub>6</sub>
            </span>
          }
          quantity={vitaminB6Quantity}
          uom={vitaminB6Uom}
          percentage={vitaminB6DailyValue}
          measurementPrecisionCode={vitaminB6PrecisionCode}
        />
        <SubNutritionRow
          title="Folate"
          quantity={folateQuantity}
          uom={folateUom}
          percentage={folateDailyValue}
          measurementPrecisionCode={folatePrecisionCode}
        />
        <SubNutritionRow
          title="Folic Acid"
          quantity={folicAcidQuantity}
          uom={folicAcidUom}
          percentage={folicAcidDailyValue}
          measurementPrecisionCode={folicAcidPrecisionCode}
          offset={1}
        />
        <SubNutritionRow
          title={
            <span>
              Vitamin B<sub>12</sub>
            </span>
          }
          quantity={vitaminB12Quantity}
          uom={vitaminB12Uom}
          percentage={vitaminB12DailyValue}
          measurementPrecisionCode={vitaminB12PrecisionCode}
        />
        <SubNutritionRow
          title="Pantothenic Acid"
          quantity={pantothenicAcidQuantity}
          uom={pantothenicAcidUom}
          percentage={pantothenicAcidDailyValue}
          measurementPrecisionCode={pantothenicAcidPrecisionCode}
        />
        <SubNutritionRow
          title="Choline"
          quantity={cholineQuantity}
          uom={cholineUom}
          percentage={cholineDailyValue}
          measurementPrecisionCode={cholinePrecisionCode}
        />
        <SubNutritionRow
          title="Phosphorous"
          quantity={phosphorousQuantity}
          uom={phosphorousUom}
          percentage={phosphorousDailyValue}
          measurementPrecisionCode={phosphorousPrecisionCode}
        />
        <SubNutritionRow
          title="Magnesium"
          quantity={magnesiumQuantity}
          uom={magnesiumUom}
          percentage={magnesiumDailyValue}
          measurementPrecisionCode={magnesiumPrecisionCode}
        />
        <SubNutritionRow
          title="Zinc"
          quantity={zincQuantity}
          uom={zincUom}
          percentage={zincDailyValue}
          measurementPrecisionCode={zincPrecisionCode}
        />
        <SubNutritionRow
          title="Selenium"
          quantity={seleniumQuantity}
          uom={seleniumUom}
          percentage={seleniumDailyValue}
          measurementPrecisionCode={seleniumPrecisionCode}
        />
        <SubNutritionRow
          title="Copper"
          quantity={copperQuantity}
          uom={copperUom}
          percentage={copperDailyValue}
          measurementPrecisionCode={copperPrecisionCode}
        />
        <SubNutritionRow
          title="Manganese"
          quantity={manganeseQuantity}
          uom={manganeseUom}
          percentage={manganeseDailyValue}
          measurementPrecisionCode={manganesePrecisionCode}
          skipSeparator
        />
        <Separator thickness={MEDIUM_SEPARATOR_THICKNESS} />

        <div className={classNames(fontStyles('12px'), 'd-flex justify-content-between')}>
          <div className="pr-1">*</div>
          <div>
            The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to
            a daily diet. 2,000 calories a day is used for general nutrition advice.
          </div>
        </div>

        {ingredients && <Separator />}
        <NutritionPanelIngredients
          ingredients={ingredients}
          className={classNames(fontStyles('12px'), 'd-flex justify-content-between')}
          labelClassName={classNames(fontStyles('12px', true), 'pr-2 text-nowrap')}
          valueClassName={classNames(fontStyles('12px', true), 'text-right')}
        />
      </div>
    </>
  );
}

ProductNutritionFactsView.propTypes = {
  nutritionAttributes: PropTypes.array,
  // From the Ingredients section, the one canonical source for ingredients.
  ingredients: PropTypes.string,
};

ProductNutritionFactsView.defaultProps = {
  ingredients: null,
};

export default ProductNutritionFactsView;
export { NUTRITION_ATTRIBUTES };
